import { useAuthStore } from '../stores/authStore';
import type { ApiResponse, VideoJob, VideoPrompt, VideoProvider, User, ProviderInfo, CostEstimate, UserStats, UserQuota } from '../types';

const API_BASE = '/api/v1';

class ApiClient {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = useAuthStore.getState().token;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Request failed');
    }

    return data;
  }

  // Auth
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return response.data!;
  }

  async register(email: string, password: string, name: string): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
    return response.data!;
  }

  // Google OAuth
  async loginWithGoogle(credential: string): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
    return response.data!;
  }

  // Video
  async generateVideo(prompt: VideoPrompt, provider?: VideoProvider): Promise<VideoJob> {
    const response = await this.request<VideoJob>('/video/generate', {
      method: 'POST',
      body: JSON.stringify({ prompt, provider }),
    });
    return response.data!;
  }

  async getJob(jobId: string): Promise<VideoJob> {
    const response = await this.request<VideoJob>(`/video/jobs/${jobId}`);
    return response.data!;
  }

  async getJobs(options?: { limit?: number; offset?: number; status?: string }): Promise<{
    jobs: VideoJob[];
    pagination: { total: number; hasMore: boolean };
  }> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.status) params.set('status', options.status);

    const response = await this.request<VideoJob[]>(`/video/jobs?${params}`);
    return {
      jobs: response.data!,
      pagination: response.meta?.pagination || { total: 0, hasMore: false },
    };
  }

  async cancelJob(jobId: string): Promise<void> {
    await this.request(`/video/jobs/${jobId}`, { method: 'DELETE' });
  }

  async getProviders(): Promise<ProviderInfo[]> {
    const response = await this.request<ProviderInfo[]>('/video/providers');
    return response.data!;
  }

  async estimateCost(prompt: VideoPrompt, provider?: VideoProvider): Promise<CostEstimate[]> {
    const response = await this.request<CostEstimate[]>('/video/estimate', {
      method: 'POST',
      body: JSON.stringify({ prompt, provider }),
    });
    return response.data!;
  }

  // User
  async getMe(): Promise<User> {
    const response = await this.request<User>('/user/me');
    return response.data!;
  }

  async getStats(): Promise<UserStats> {
    const response = await this.request<UserStats>('/user/me/stats');
    return response.data!;
  }

  async getQuota(): Promise<UserQuota> {
    const response = await this.request<UserQuota>('/user/me/quota');
    return response.data!;
  }
}

export const api = new ApiClient();
